/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useAuth } from '@/providers/auth';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';
import { createLogger } from '@/lib/logger';
import { setWertSdkInstance, getWertSdkInstance, type WertSdkInstance } from '@/lib/adapters/engagement/wert';

const logger = createLogger({ scope: 'wert-bootstrap' });

/**
 * `sessionStorage` idempotency key — mirrors the GTM `pushSessionStart`
 * pattern. Prevents `init()` from firing twice after client-side navigation
 * or a hard refresh within the same tab.
 *
 * We keep the key here (not in the SDK adapter) because the guard is a
 * bootstrap concern, not an adapter concern.
 */
const WERT_INITIALIZED_KEY = 'wert_initialized';

const QA_HOSTNAME_PREFIXES = ['staging.', 'development.'];
// Both localhost and *.exp-delivery.com (MRT sandboxes) route through the
// local BFF proxy (environment: 'dev') so the browser never hits
// qa-wert.residenthome.com directly, avoiding CORS rejections.
const PROXY_HOSTNAMES = new Set(['localhost']);
const PROXY_HOSTNAME_SUFFIXES = ['.exp-delivery.com', '.commercecloud.salesforce.com'];

/**
 * Resolve the WERT SDK environment.
 *
 * 1. If config explicitly sets it, use that.
 * 2. `localhost` / `*.exp-delivery.com` → `'dev'`: the vendor SDK routes
 *    `dev` to `/wert-proxy` (same-origin BFF) instead of hitting
 *    qa-wert.residenthome.com directly, bypassing the CORS restriction.
 * 3. `staging.*` / `development.*` → `'qa'`.
 * 4. Everything else → `'production'`.
 */
function resolveEnvironment(
    override: 'qa' | 'production' | 'rc' | 'dev' | undefined,
    hostname: string
): 'qa' | 'production' | 'rc' | 'dev' {
    if (override) return override;
    if (PROXY_HOSTNAMES.has(hostname)) return 'dev';
    if (PROXY_HOSTNAME_SUFFIXES.some((suffix) => hostname.endsWith(suffix))) return 'dev';
    if (QA_HOSTNAME_PREFIXES.some((prefix) => hostname.startsWith(prefix))) return 'qa';
    return 'production';
}

/**
 * Read cookies from `document.cookie` into a `{ name: value }` map.
 * Mirrors Ashley Next `wert.ts` `getCookies()`. Silent on parse errors — the
 * cookie header format is very forgiving.
 */
function readCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    if (typeof document === 'undefined') return cookies;
    for (const raw of document.cookie.split(';')) {
        const trimmed = raw.trim();
        if (!trimmed) continue;
        const eq = trimmed.indexOf('=');
        if (eq < 0) continue;
        const name = trimmed.slice(0, eq).trim();
        const value = trimmed.slice(eq + 1).trim();
        if (!name) continue;
        try {
            cookies[name] = decodeURIComponent(value);
        } catch {
            // Malformed URI component — keep the raw value rather than dropping
            // the cookie. Fail-open: session-level data is best-effort.
            cookies[name] = value;
        }
    }
    return cookies;
}

/** Read a single cookie by name; empty string when absent. */
function readCookie(name: string): string {
    if (typeof document === 'undefined') return '';
    const cookies = readCookies();
    return cookies[name] ?? '';
}

/**
 * Serialize UTM parameters from the current URL as a JSON string. Matches
 * Ashley Next `wert.ts` `getUtmJourney()` — the SDK expects a string, not an
 * object.
 */
function serializeUtmJourney(): string {
    if (typeof window === 'undefined') return '{}';
    const params = new URLSearchParams(window.location.search);
    return JSON.stringify({
        utm_source: params.get('utm_source') ?? '',
        utm_medium: params.get('utm_medium') ?? '',
        utm_campaign: params.get('utm_campaign') ?? '',
        utm_term: params.get('utm_term') ?? '',
        utm_content: params.get('utm_content') ?? '',
    });
}

/**
 * WertBootstrap — client-only SDK initialization component.
 *
 * Renders nothing. On first mount after tracking consent = Accepted, it:
 *   1. Dynamically imports `@spreespace/wert-sdk` (keeps the SDK out of the
 *      pre-consent bundle).
 *   2. Calls `init({ auth_token, brand, environment, session_id })`.
 *   3. Calls `initSessionLevelData(...)` with browser / cookie / UTM context.
 *   4. Calls `sendInitialSiteWideEvent(...)` — the WERT-only session-scoped event.
 *   5. Hands the initialized SDK instance to the adapter singleton via
 *      `setWertSdkInstance(...)` so Phase 2 event trackers can find it.
 *   6. Sets a `sessionStorage.wert_initialized` sentinel to guard against
 *      re-init on subsequent renders in the same tab.
 *
 * Fail-open error handling: every SDK call is wrapped in `try/catch`; a WERT
 * failure logs a warning and no-ops. This is a deliberate exception to
 * CLAUDE.md §2 — analytics adapters must not surface errors to the shopper.
 */
export function WertBootstrap({ authToken: authTokenProp }: { authToken?: string }) {
    const config = useConfig<AppConfig>();
    const auth = useAuth();
    const { trackingConsent } = useTrackingConsent();

    const wertConfig = config.engagement?.adapters?.wert;
    const isEnabled = wertConfig?.enabled ?? false;
    const hasConsent = trackingConsent === TrackingConsent.Accepted;
    const canInit = isEnabled && hasConsent;

    // sessionStorage guard is checked once per mount inside the effect. Ref
    // prevents the effect body from re-running the async init if React 19
    // strict-mode re-renders while the promise chain is inflight.
    const bootstrapStartedRef = useRef(false);

    // Recovery path — runs as useLayoutEffect so the sdkInstance singleton is
    // restored BEFORE any child useEffect (e.g. checkout tracking effects) fires.
    // useEffect fires children-before-parents, so a useEffect here would lose
    // the race against trackInitiateCheckout / trackAddShippingInfo on the
    // checkout page. useLayoutEffect runs synchronously after DOM mutations,
    // parent-before-children — the opposite order — guaranteeing the instance
    // is available when the checkout effects read getWertSdkInstance().
    //
    // This covers the Vite HMR case: when wert-adapter.ts hot-reloads, sdkInstance
    // resets to null but window.WERT_SDK (set by the SDK itself on init) survives.
    useLayoutEffect(() => {
        if (typeof window === 'undefined') return;
        if (!canInit) return;
        const existing = (window as unknown as { WERT_SDK?: WertSdkInstance }).WERT_SDK;
        if (existing && !getWertSdkInstance()) {
            setWertSdkInstance(existing);
        }
    }, [canInit]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!canInit) return;
        if (bootstrapStartedRef.current) return;

        // Capture session id + browser context ahead of the async import so
        // hooks don't re-read them once the effect body is `await`ing.
        const sessionId = auth?.usid ?? '';
        if (!sessionId) {
            logger.warn('WERT bootstrap skipped — no SLAS usid on auth context');
            return;
        }
        // Prefer the prop injected from the server loader (never serialized to
        // window.__APP_CONFIG__) over the config value (which has authToken stripped).
        const authToken = authTokenProp ?? wertConfig?.authToken ?? '';
        if (!authToken) {
            logger.warn('WERT bootstrap skipped — no authToken in config');
            return;
        }

        // Guard is set only after both pre-conditions pass so a retry is
        // possible if auth wasn't ready on the first canInit=true render.
        bootstrapStartedRef.current = true;

        const hostname = window.location.hostname;
        const environment = resolveEnvironment(wertConfig?.environment, hostname);
        logger.debug('WERT environment resolution', {
            configOverride: wertConfig?.environment,
            hostname,
            resolved: environment,
        });

        // Dynamic import: defers ~8kb gzipped SDK cost until after consent.
        // Fail-open: any failure logs and no-ops; the storefront keeps running.
        void (async () => {
            try {
                const { init } = await import('@spreespace/wert-sdk');

                const sdk = init({
                    auth_token: authToken,
                    brand: wertConfig?.brand ?? 'ashley',
                    environment,
                    session_id: sessionId,
                });

                if (!sdk) {
                    logger.warn('WERT SDK init returned undefined (non-browser?)');
                    return;
                }

                const initialSessionData = {
                    gauid: readCookie('optimizely_user_id'),
                    platform_ip: '',
                    cookies: readCookies(),
                    _fbp: readCookie('_fbp'),
                    _fbc: readCookie('_fbc'),
                    _ga: readCookie('_ga'),
                    utm_string: window.location.search,
                    utm_journey: serializeUtmJourney(),
                    ttclid: new URLSearchParams(window.location.search).get('ttclid') ?? '',
                    _dyid: '',
                    brands_ip: '',
                    next_ip: '',
                    rh_client_id: '',
                    original_user_id: '',
                    // `gtm_loaded` is a hint the SDK writes to session storage
                    // so Gather-side attribution can tell whether GTM was up.
                    // Best-effort read of GTM's own load marker in dataLayer.
                    gtm_loaded: (window as unknown as { google_tag_manager?: unknown }).google_tag_manager
                        ? 'true'
                        : 'false',
                };

                try {
                    sdk.initSessionLevelData(initialSessionData);
                } catch (error) {
                    // Fail-open: log + swallow. WERT must not crash the storefront.
                    logger.warn('WERT initSessionLevelData failed', { error });
                }

                try {
                    const initialSiteWideEvent = {
                        type: 'nonInteraction' as const,
                        event_name: 'initial_sitewide',
                        page_path: window.location.pathname + window.location.search,
                        browser: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
                        timezone:
                            (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || '',
                        country: '',
                        state: '',
                        city: '',
                        postal_code: '',
                        referral_page: document.referrer || '',
                        screen_resolution: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : '',
                    };
                    // Second arg `true` mirrors this WERT-only event into
                    // `window.dataLayer` so marketing can build a GTM tag on it.
                    sdk.sendInitialSiteWideEvent(initialSiteWideEvent, true);
                } catch (error) {
                    // Fail-open: analytics events must not raise.
                    logger.warn('WERT sendInitialSiteWideEvent failed', { error });
                }

                // Register the initialized SDK with the adapter singleton so
                // Phase 2 per-event trackers can dispatch through it. The
                // adapter's local type widens the SDK's typed event shapes to
                // `Record<string, unknown>` so the shell can accept any WERT
                // event without a per-event type on the mediator; the double
                // cast is intentional here.
                setWertSdkInstance(sdk as unknown as WertSdkInstance);

                try {
                    window.sessionStorage.setItem(WERT_INITIALIZED_KEY, '1');
                } catch {
                    // sessionStorage unavailable — same rationale as the earlier
                    // guard. Not fatal, just log at debug.
                    logger.debug('WERT sessionStorage guard could not be set');
                }
            } catch (error) {
                // Fail-open: SDK import / init failed. Log and continue.
                // This is the deliberate exception to CLAUDE.md §2 — analytics
                // adapters must not surface errors to the shopper.
                logger.error('WERT bootstrap failed', { error });
            }
        })();
    }, [canInit, auth?.usid, authTokenProp, wertConfig?.authToken, wertConfig?.brand, wertConfig?.environment]);

    return null;
}
